import moment from 'moment';
import { getUIFieldTypeFromDataElement } from 'dsilo-ui-components';

import { isConfigEnabled } from './config/isConfigEnabled';
import {
    applyIdmTierReadOnlyToColumnHeaders,
    getTierControlField,
    getTierTriggerFields,
} from './idmTierRules';
import {
    getNegotiationStatusDependencyFields,
    getNegotiationStatusOptions,
} from './rules/negotiationStatusRules';
import {
    isApiFetchSelectValueEmpty,
    parseApiFetchSelectDisplayValue,
} from '../TrackerViewerV2/utils/apiFetchSelect';
import { formatConfiguredAmountValue, enrichNumberElementForInlineEdit } from '../TrackerViewerV2/utils/formatters';
import { formatDealReviewHistoryTimePST } from '../TrackerViewer/utils';
import {
    getTierResetConfigFieldKeys,
    isTierResetEnabledForField,
} from '../AdminEditableList/tierResetRules';
import {
    getTierDateMessageForField,
    getTierDateValidationMode,
    validateTierReviewDates,
} from '../AdminEditableList/tierRules';

export {
    getTierResetConfigFieldKeys,
    isTierResetEnabledForField,
    getTierDateMessageForField,
    getTierDateValidationMode,
    validateTierReviewDates,
};

/**
 * Returns true when a column should be hidden from the table UI.
 */
export const isColumnHidden = (column = {}) => {
    const flag = column.isHide;

    return flag === true || flag === 'true' || flag === 'True';
};

/**
 * Returns true when field history is enabled for a column.
 */
export const isHistoryEnabledColumn = (column = {}, behaviorConfig) => {
    if (!isConfigEnabled(behaviorConfig?.features?.fieldHistory)) {
        return false;
    }

    return Boolean(column.isHistory) && Boolean(column.showHistroy);
};

/**
 * Resolves the history array key for a configured field.
 */
export const getFieldHistoryKey = (column = {}) => {
    const fieldKey = getCellFieldKey(column);
    return fieldKey ? `${fieldKey}History` : null;
};

/**
 * Builds payload fragment that appends a field-history entry when history is enabled for the column.
 * Mirrors TrackerViewer field history tracking on value change.
 */
export const buildFieldHistoryAppendPayload = (
    row = {},
    column = {},
    value,
    updatedBy = 'Unknown',
    behaviorConfig,
) => {
    if (!isHistoryEnabledColumn(column, behaviorConfig)) {
        return {};
    }

    const historyKey = getFieldHistoryKey(column);
    if (!historyKey) {
        return {};
    }

    const existingHistory = Array.isArray(row[historyKey]) ? row[historyKey] : [];

    return {
        [historyKey]: [
            ...existingHistory,
            {
                value: value || '',
                updatedAtPST: formatDealReviewHistoryTimePST(new Date()),
                updatedBy: updatedBy || 'Unknown',
            },
        ],
    };
};

/**
 * Returns visible table columns (excludes isHide columns).
 */
export const getVisibleColumnHeaders = (blockHeaders = []) => (
    blockHeaders.filter((column) => !isColumnHidden(column))
);

/**
 * Returns true when a column is configured to stay pinned during horizontal scroll.
 */
export const isColumnSticky = (column = {}) => {
    const flag = column.isSticky;

    return flag === true || flag === 'true' || flag === 'True';
};

/**
 * Reorders columns so every `isSticky` column comes first (relative order preserved
 * within each group), without mutating the input array.
 */
export const getStickyOrderedColumns = (columns = []) => {
    const stickyColumns = columns.filter((column) => isColumnSticky(column));
    const nonStickyColumns = columns.filter((column) => !isColumnSticky(column));

    return [...stickyColumns, ...nonStickyColumns];
};

/**
 * Builds the comma-separated fields string for table API requests.
 */
export const buildFieldsFromSelectedElements = (
    selectedDataElement = [],
    behaviorConfig,
) => {
    const fieldNames = [];
    const tierResetConfig = behaviorConfig?.tierResetLegacy || [];
    const controlField = getTierControlField(selectedDataElement, behaviorConfig);

    selectedDataElement.forEach((element) => {
        if (element?.value) {
            fieldNames.push(element.value);
        }

        if (element?.name && element.name !== element.value) {
            fieldNames.push(element.name);
        }

        if (isHistoryEnabledColumn(element, behaviorConfig)) {
            const historyKey = getFieldHistoryKey(element);
            if (historyKey) {
                fieldNames.push(historyKey);
            }
        }
    });

    if (isConfigEnabled(behaviorConfig?.tier?.autoCalculation)) {
        getTierTriggerFields(behaviorConfig).forEach((fieldKey) => {
            fieldNames.push(fieldKey);
        });
    }

    if (controlField && !fieldNames.includes(controlField)) {
        fieldNames.push(controlField);
    }

    if (!fieldNames.includes('_id')) {
        fieldNames.unshift('_id');
    }

    if (!fieldNames.includes('updatedDate')) {
        fieldNames.push('updatedDate');
    }

    if (isConfigEnabled(behaviorConfig?.negotiationStatus)) {
        getNegotiationStatusDependencyFields(behaviorConfig).forEach((fieldKey) => {
            fieldNames.push(fieldKey);
        });
    }

    getTierResetConfigFieldKeys(tierResetConfig).forEach((fieldKey) => {
        fieldNames.push(fieldKey);
    });

    return [...new Set(fieldNames)].filter(Boolean).join(',');
};

/**
 * Merges chart-level conditions with runtime filter conditions.
 */
export const mergeTableConditions = (baseConditions = [], filterConditions = []) => (
    [...(baseConditions || []), ...(filterConditions || [])]
);

/**
 * Normalizes chart sort config to a single-field API sort: { fieldName: 1 | -1 }.
 */
export const normalizeChartSort = (sortConfig) => {
    if (!sortConfig || typeof sortConfig !== 'object' || Array.isArray(sortConfig)) {
        return {};
    }

    const sortEntry = Object.entries(sortConfig).find(([, direction]) => {
        const numericDirection = Number(direction);
        return numericDirection === 1 || numericDirection === -1;
    });

    if (!sortEntry) {
        return {};
    }

    const [fieldKey, direction] = sortEntry;
    if (!fieldKey) {
        return {};
    }

    return {
        [fieldKey]: Number(direction) === -1 ? -1 : 1,
    };
};

/**
 * Applies negotiationStatus dropdown options from behavior config.
 */
const applyNegotiationStatusOptions = (column = {}, behaviorConfig) => {
    const columnKey = getCellFieldKey(column);
    const statusField = behaviorConfig?.negotiationStatus?.field || 'negotiationStatus';

    if (columnKey !== statusField || !isConfigEnabled(behaviorConfig?.negotiationStatus)) {
        return column;
    }

    return {
        ...column,
        options: getNegotiationStatusOptions(behaviorConfig),
    };
};

/**
 * Enriches configured column definitions with data-model metadata.
 */
export const enrichColumnHeaders = (
    selectedDataElement = [],
    dataElements = [],
    behaviorConfig,
) => {
    if (!selectedDataElement?.length) {
        return [];
    }

    const mergedHeaders = JSON.parse(JSON.stringify(selectedDataElement)).map((column) => {
        const columnKey = column.value || column.name;
        const schemaElement = dataElements.find(
            (dataElement) => dataElement.name === columnKey
                || dataElement.name === column.value
                || dataElement.name === column.name,
        );

        if (!schemaElement?.name) {
            return applyNegotiationStatusOptions(column, behaviorConfig);
        }

        return applyNegotiationStatusOptions({ ...schemaElement, ...column }, behaviorConfig);
    });

    return applyIdmTierReadOnlyToColumnHeaders(mergedHeaders, selectedDataElement, behaviorConfig);
};

/**
 * Resolves column config from enriched headers, falling back to selectedDataElement.
 */
export const resolveColumnConfig = (fieldKey, blockHeaders = [], selectedDataElement = []) => {
    const headerMatch = blockHeaders.find((column) => getCellFieldKey(column) === fieldKey);
    if (headerMatch) {
        return headerMatch;
    }

    return selectedDataElement.find((column) => getCellFieldKey(column) === fieldKey) || null;
};

/**
 * Formats a read-only table cell value (includes amount/currency display rules).
 */
export const formatTableCellDisplayValue = (value, column = {}) => (
    formatCellDisplayValue(value, column)
);

/**
 * Formats a cell value for read-only table display.
 */
export const formatCellDisplayValue = (value, element = {}) => {
    const displayElement = enrichNumberElementForInlineEdit(element);

    if (value === null || value === undefined || value === '') {
        return '—';
    }

    if (displayElement.isFetchAPI) {
        const selectedOption = parseApiFetchSelectDisplayValue(value, displayElement);
        if (!selectedOption) {
            return '—';
        }

        return selectedOption.label || selectedOption.value || '—';
    }

    if (displayElement.type === 'namepicker' && typeof value === 'object') {
        return value.label || value.name || value.value || '—';
    }

    if (Array.isArray(value)) {
        return value.map((item) => (typeof item === 'object' ? item.label || item.name || item.value : item)).join(', ');
    }

    if (displayElement.type === 'date' || displayElement.type === 'datetime') {
        const parsed = moment.parseZone(value);
        return parsed.isValid() ? parsed.format('L') : String(value);
    }

    const configuredAmountDisplay = formatConfiguredAmountValue(value, displayElement);
    if (configuredAmountDisplay != null) {
        return configuredAmountDisplay;
    }

    if (displayElement.showDollarSymbol || displayElement.showDoller) {
        const numeric = Number(String(value).replace(/[$,]/g, ''));
        return Number.isNaN(numeric) ? String(value) : `$${numeric.toLocaleString()}`;
    }

    return String(value);
};

/**
 * Resolves the data key used for a column/cell.
 */
export const getCellFieldKey = (column = {}) => column.value || column.name;

/**
 * Returns true when a column is configured as non-editable in the admin table.
 */
export const isColumnNonEditable = (column = {}) => {
    const flag = column.isNonEditable;

    return flag === true || flag === 'true' || flag === 'True';
};

/**
 * Returns true when a column is configured as required.
 */
export const isColumnRequired = (column = {}, behaviorConfig) => {
    if (!isConfigEnabled(behaviorConfig?.validation?.required)) {
        return false;
    }

    const flag = column.isRequired ?? column.required;

    return flag === true || flag === 'true' || flag === 'True';
};

/**
 * Returns true when a required field value should be treated as empty.
 */
export const isRequiredFieldValueEmpty = (value, column = {}) => {
    if (column?.isFetchAPI) {
        return isApiFetchSelectValueEmpty(value);
    }

    if (value === null || value === undefined || value === '') {
        return true;
    }

    if (typeof value === 'string' && value.trim() === '') {
        return true;
    }

    if (Array.isArray(value) && value.length === 0) {
        return true;
    }

    if (typeof value === 'object') {
        const innerValue = value.value ?? value.name ?? value.label;
        return innerValue === null || innerValue === undefined || innerValue === '';
    }

    return false;
};

/**
 * Returns the display label used in user-facing messages for a column.
 */
export const getColumnDisplayLabel = (column = {}) => column.label || column.value || column.name || 'this';

/**
 * Returns the toast message when a required field is saved empty.
 */
export const getRequiredFieldValidationMessage = (column = {}) => (
    `${getColumnDisplayLabel(column)} is required.`
);

/**
 * Returns the toast message when a non-editable field is clicked.
 */
export const getNonEditableValidationMessage = (column = {}, behaviorConfig) => {
    const template = behaviorConfig?.validation?.nonEditable?.message
        || 'You cannot modify {fieldLabel} because it is not editable.';

    return template.replace('{fieldLabel}', getColumnDisplayLabel(column));
};

/**
 * Compares two cell values for equality before persisting.
 */
export const areCellValuesEqual = (left, right) => {
    if (left === right) {
        return true;
    }
    if (left === null || left === undefined || left === '') {
        return right === null || right === undefined || right === '';
    }
    if (typeof left === 'object' || typeof right === 'object') {
        return JSON.stringify(left) === JSON.stringify(right);
    }
    return String(left) === String(right);
};

/**
 * Ensures cleared select fields persist as null instead of undefined/empty string.
 */
export const normalizeSelectSaveValue = (value, element = {}) => {
    const fieldType = getUIFieldTypeFromDataElement(element);

    if (fieldType !== 'select' && fieldType !== 'namepicker' && !element?.isFetchAPI) {
        return value;
    }

    if (value === undefined || value === '') {
        return null;
    }

    return value;
};

/**
 * Field types that save immediately when their value changes.
 */
export const shouldAutoSaveOnChange = (element = {}) => {
    const fieldType = getUIFieldTypeFromDataElement(element);

    switch (fieldType) {
        case 'select':
        case 'namepicker':
        case 'checkbox':
            return true;
        default:
            return Boolean(element?.isFetchAPI);
    }
};

/**
 * Builds a stable DOM selector token for the active editing cell.
 */
export const getEditingCellAttr = (rowId, fieldKey) => `${rowId}__${fieldKey}`;

/**
 * Returns true when the column renders a dropdown-style editor.
 */
export const isDropdownColumn = (column = {}) => {
    const fieldType = getUIFieldTypeFromDataElement(column);

    return fieldType === 'select'
        || fieldType === 'namepicker'
        || Boolean(column?.isFetchAPI);
};

/**
 * Ignores click-away events that originate from portaled menus/popovers.
 */
export const isIgnoredClickAwayTarget = (target) => {
    if (!(target instanceof Element)) {
        return false;
    }

    return Boolean(
        target.closest('.MuiPopover-root')
        || target.closest('.MuiMenu-paper')
        || target.closest('.MuiMenu-list')
        || target.closest('.MuiDialog-root')
        || target.closest('.MuiPickersPopper-root')
        || target.closest('[role="listbox"]')
        || target.closest('[role="option"]')
        || target.closest('[class*="-menu"]')
        || target.closest('[class*="-Menu"]')
        || target.closest('[id*="react-select"]')
        || target.closest('.api-select-menu-panel'),
    );
};

/**
 * Returns true when Escape should close an overlay first instead of cancelling inline edit.
 * Narrower than isIgnoredClickAwayTarget — does not match closed react-select controls.
 */
export const shouldDeferInlineEditEscapeCancel = (target, isSelectMenuOpen) => {
    if (isSelectMenuOpen) {
        return true;
    }

    if (!(target instanceof Element)) {
        return false;
    }

    if (target.closest('.MuiPickersPopper-root')) {
        return true;
    }

    if (target.closest('.MuiDialog-root')) {
        return true;
    }

    if (target.closest('[role="listbox"]')) {
        return true;
    }

    if (target.closest('.api-select-menu-panel')) {
        return true;
    }

    if (target.closest('.MuiMenu-paper')) {
        return true;
    }

    const popoverRoot = target.closest('.MuiPopover-root');
    if (popoverRoot && popoverRoot.getAttribute('aria-hidden') !== 'true') {
        return true;
    }

    return false;
};

/**
 * Returns true when the key event is Escape.
 */
export const isEscapeKeyEvent = (event) => event?.key === 'Escape' || event?.keyCode === 27;
